import { useMenu, useRefinementList } from 'react-instantsearch';
import { Popover } from '@headlessui/react';
import { oneLine } from 'common-tags';

import ChevronDown from '@/icons/light/chevron-down.svg';
import ChevronUp from '@/icons/light/chevron-up.svg';

import type { FunctionComponent } from 'react';

type FacetItem = {
  label: string;
  value: string;
  count: number;
  isRefined: boolean;
};

// `w-max min-w-44` es lo que necesita la fila que scrollea en mobile: cada
// control conserva su ancho natural con un mínimo legible.
//
// En lg manda la columna del grid de la barra de filtros. Sin `lg:w-full
// lg:min-w-0`, ese mínimo de 11rem (176px) hacía que el botón se saliera de su
// celda en viewports estrechos —a 1211px la columna mide 137px— y se montara
// sobre el filtro siguiente.
const buttonClass = (open: boolean) =>
  oneLine`text-placeholder bg-neutrals-0 relative w-max min-w-44 rounded-full border py-3 pr-10 pl-4 text-left lg:w-full lg:min-w-0 ${
    open ? 'border-neutrals-900' : 'border-neutrals-500'
  }`;

// La tipografía va declarada en el panel (no se hereda del wrapper) y el
// panel es absolute igual que el HierarchicalMenu de la misma barra.
const panelClass =
  'text-body text-body-s rounded-custom border-neutrals-100 bg-bg-neutrals-0 shadow-light-s absolute z-20 mt-1 max-h-64 w-max min-w-full overflow-y-auto border p-1';

const rowClass = (active: boolean) =>
  oneLine`hover:bg-neutrals-50 flex w-full cursor-pointer items-center justify-between gap-4 py-3 pr-2 pl-3 text-left ${
    active ? 'text-text-neutrals-900 font-bold' : 'text-text-neutrals-500'
  }`;

/**
 * Select de un solo valor sobre un facet de Algolia (useMenu): pensado para
 * la barra de filtros del listado (Tipo, Baños, Estado...). Muestra counts
 * y deselecciona al volver a elegir el valor activo o "Cualquiera".
 */
export const FacetSelect: FunctionComponent<{
  attribute: string;
  placeholder: string;
  className?: string;
  clearLabel?: string;
  formatLabel?: (label: string) => string;
  sortBy?: Parameters<typeof useMenu>[0]['sortBy'];
  /**
   * Registra el widget (para que la URL filtre) pero no pinta el control.
   * Se usa en tenants sin chrome extendido: el filtro sigue siendo
   * URL-aplicable de forma consistente aunque no haya UI para tocarlo.
   */
  hidden?: boolean;
}> = ({
  attribute,
  placeholder,
  className = '',
  clearLabel = 'Cualquiera',
  formatLabel = (label) => label,
  sortBy = ['name:asc'],
  hidden = false,
}) => {
  const { items, refine } = useMenu({ attribute, sortBy, limit: 30 });
  const refined = items.find((item) => item.isRefined);

  if (hidden) return null;

  return (
    <Popover className={`relative shrink-0 ${className}`}>
      {({ open, close }) => {
        const Icon = open ? ChevronUp : ChevronDown;
        return (
          <>
            <Popover.Button className={buttonClass(open)}>
              <span className="block truncate">
                {refined ? formatLabel(refined.label) : placeholder}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <Icon className="h-6 w-6" aria-hidden="true" />
              </span>
            </Popover.Button>

            <Popover.Panel className={panelClass}>
              <ul className="divide-neutrals-50 divide-y">
                <li>
                  <button
                    type="button"
                    className={rowClass(!refined)}
                    onClick={() => {
                      if (refined) refine(refined.value);
                      close();
                    }}
                  >
                    {clearLabel}
                  </button>
                </li>
                {items.map((item: FacetItem) => (
                  <li key={item.value}>
                    <button
                      type="button"
                      className={rowClass(item.isRefined)}
                      onClick={() => {
                        refine(item.value);
                        close();
                      }}
                    >
                      <span className="truncate">
                        {formatLabel(item.label)}
                      </span>
                      <span className="text-text-neutrals-300">
                        {item.count}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </Popover.Panel>
          </>
        );
      }}
    </Popover>
  );
};

FacetSelect.displayName = 'FacetSelect';

/**
 * Multi-select con checkboxes sobre un facet (useRefinementList, operador
 * AND): pensado para "Características". `hideValues` filtra entradas
 * internas de SAP que no son características de cara al usuario.
 */
export const FacetMultiSelect: FunctionComponent<{
  attribute: string;
  placeholder: string;
  className?: string;
  hideValues?: string[];
  formatLabel?: (label: string) => string;
  /** Registra el widget sin pintar el control (ver `FacetSelect.hidden`). */
  hidden?: boolean;
}> = ({
  attribute,
  placeholder,
  className = '',
  hideValues = [],
  formatLabel = (label) => label,
  hidden = false,
}) => {
  const { items, refine } = useRefinementList({
    attribute,
    operator: 'and',
    limit: 30,
    sortBy: ['name:asc'],
  });
  const visible = items.filter((item) => !hideValues.includes(item.value));
  const refinedCount = visible.filter((item) => item.isRefined).length;

  if (hidden) return null;

  return (
    <Popover className={`relative shrink-0 ${className}`}>
      {({ open }) => {
        const Icon = open ? ChevronUp : ChevronDown;
        return (
          <>
            <Popover.Button className={buttonClass(open)}>
              <span className="block truncate">
                {refinedCount > 0
                  ? `${placeholder} (${refinedCount})`
                  : placeholder}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <Icon className="h-6 w-6" aria-hidden="true" />
              </span>
            </Popover.Button>

            <Popover.Panel className={panelClass}>
              <ul className="divide-neutrals-50 divide-y">
                {visible.map((item) => (
                  <li key={item.value}>
                    <label className={rowClass(item.isRefined)}>
                      <span className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={item.isRefined}
                          onChange={() => refine(item.value)}
                          className="border-neutrals-500 checked:text-primary-500 h-4 w-4 cursor-pointer rounded-[2px] checked:ring-0 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-hidden"
                        />
                        <span className="truncate">
                          {formatLabel(item.label)}
                        </span>
                      </span>
                      <span className="text-text-neutrals-300">
                        {item.count}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </Popover.Panel>
          </>
        );
      }}
    </Popover>
  );
};

FacetMultiSelect.displayName = 'FacetMultiSelect';
